import type { PositionSummary, Venue } from "@papertiger/contracts";

/** Closed-position profit classifications available to the user. */
export type ClosedPositionProfitFilter =
  | "all"
  | "profit"
  | "loss"
  | "break-even";

/** Selected closed-position table filters. */
export interface ClosedPositionFilters {
  /** Venue selection, or all venues. */
  venue: Venue | "all";
  /** Exact outcome/contract label, or every contract. */
  contract: string;
  /** Exact contributing order type, or every order type. */
  orderType: string;
  /** Serialized exact acquisition price, or every price. */
  price: string;
  /** Realized profit classification. */
  profit: ClosedPositionProfitFilter;
}

/** Unique values exposed by the closed-position filter controls. */
export interface ClosedPositionFilterOptions {
  /** Venues represented in the current closed-position rows. */
  venues: Venue[];
  /** Contract labels represented in the current closed-position rows. */
  contracts: string[];
  /** Order-type labels represented in the current closed-position rows. */
  orderTypes: string[];
  /** Acquisition prices represented in the current closed-position rows. */
  pricesPercent1: Array<number | null>;
}

/** Default selection that displays every closed position. */
export const DEFAULT_CLOSED_POSITION_FILTERS: ClosedPositionFilters = {
  venue: "all",
  contract: "all",
  orderType: "all",
  price: "all",
  profit: "all",
};

/**
 * Serializes an optional acquisition price for an HTML select value.
 *
 * @param pricePercent1 Acquisition price on scale 1.
 * @returns Stable filter value.
 */
export function serializeClosedPositionPrice(
  pricePercent1: number | null,
): string {
  return pricePercent1 === null ? "none" : `${pricePercent1}`;
}

/**
 * Filters closed positions using exact values selected from the visible rows.
 *
 * @param positions Closed position rows.
 * @param filters Current user selections.
 * @returns Matching closed positions in their original order.
 */
export function filterClosedPositions(
  positions: PositionSummary[],
  filters: ClosedPositionFilters,
): PositionSummary[] {
  return positions.filter((position) => {
    if (filters.venue !== "all" && position.venue !== filters.venue) {
      return false;
    }
    if (filters.contract !== "all" && position.outcomeName !== filters.contract) {
      return false;
    }
    if (
      filters.orderType !== "all" &&
      (position.orderType ?? "none") !== filters.orderType
    ) {
      return false;
    }
    if (
      filters.price !== "all" &&
      serializeClosedPositionPrice(
        position.averageAcquisitionPricePercent1,
      ) !== filters.price
    ) {
      return false;
    }
    switch (filters.profit) {
      case "profit":
        return position.realizedProfitDollars > 0;
      case "loss":
        return position.realizedProfitDollars < 0;
      case "break-even":
        return position.realizedProfitDollars === 0;
      case "all":
        return true;
    }
  });
}

/**
 * Collects sorted unique filter values from closed position rows.
 *
 * @param positions Closed position rows.
 * @returns Exact values available to the filter controls.
 */
export function getClosedPositionFilterOptions(
  positions: PositionSummary[],
): ClosedPositionFilterOptions {
  const venues = [...new Set(positions.map((position) => position.venue))].sort();
  const contracts = [
    ...new Set(positions.map((position) => position.outcomeName)),
  ].sort((left, right) => left.localeCompare(right, "en-US"));
  const orderTypes = [
    ...new Set(positions.map((position) => position.orderType ?? "none")),
  ].sort((left, right) => left.localeCompare(right, "en-US"));
  const pricesPercent1 = [
    ...new Set(
      positions.map((position) => position.averageAcquisitionPricePercent1),
    ),
  ].sort((left, right) => {
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return left - right;
  });
  return { venues, contracts, orderTypes, pricesPercent1 };
}
